import { useT } from '@transifex/react'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  FulfilmentDetail,
  FulfilmentType,
} from '../../../../../../models/fulfilment'
import { useSaleManager } from '../../../../../../ng-compat/hooks'
import { setSaleFulfilmentDetail } from '../../../../../../state/slices/currentSale/currentSaleSlice'
import {
  HEAP_DATA_TRACK_UPDATE_BILLING_ADDRESS,
  HEAP_DATA_TRACK_USE_BILLING_ADDRESS_FOR_SHIPPING,
} from '../../../../sale-model.constant'
import {
  AddressErrorsStatus,
  useFulfillmentHelper,
} from '../../popover/hooks/useFulfillmentHelper'
import { EditAddressButtons } from '../EditAddressButton/EditAddressButton.type'
import type { AddOrEditAddressButtonType } from '../EditAddressButton/EditAddressButton.type'
import { useAddressModalHandlers } from '../hooks/useAddressModalHandlers'

export interface FormButtonOnEmptyShippingAddressProps {
  loading?: boolean
  fulfilmentDetail: FulfilmentDetail
  handleEmptyShippingCorrectBillingAddress?: () => void
  onClose?: () => void
  onSave?: (fulfillmentDetails: FulfilmentDetail[]) => void
}

export const useFormButtonOnEmptyShippingAddressModel = ({
  fulfilmentDetail,
  handleEmptyShippingCorrectBillingAddress,
  onClose,
  onSave,
}: FormButtonOnEmptyShippingAddressProps) => {
  const t = useT()
  const { validateFulfillmentAddresses } = useFulfillmentHelper()
  const { error, status } = validateFulfillmentAddresses(fulfilmentDetail)
  const dispatch = useDispatch()
  const saleManager = useSaleManager()
  const [isClickedToOpenModal, setIsClickedToOpenModal] = useState(false)

  const hasIncorrectShippingOrBillingAddress =
    status === AddressErrorsStatus.INCORRECTSHIPPING_INCORRECTBILLING ||
    status === AddressErrorsStatus.CORRECTSHIPPING_INCORRECTBILLING ||
    status === AddressErrorsStatus.INCORRECTSHIPPING_CORRECTBILLING

  const buttonType: AddOrEditAddressButtonType = hasIncorrectShippingOrBillingAddress
    ? EditAddressButtons.edit
    : EditAddressButtons.add

  const { openAddressModal } = useAddressModalHandlers({
    type: buttonType,
    onClose,
    onSave,
  })

  const { openAddressModal: openEditAddressModal } = useAddressModalHandlers({
    type: EditAddressButtons.edit,
    onClose,
    onSave,
  })

  const copyBillingAddressToShippingAddress = () => {
    const newFulfillmentDetail = {
      type: FulfilmentType.DELIVERY,
      note: fulfilmentDetail?.note || '',
      billingAddress: fulfilmentDetail?.billingAddress,
      shippingAddress:
        fulfilmentDetail?.billingAddress || fulfilmentDetail?.shippingAddress,
    }

    dispatch(setSaleFulfilmentDetail(newFulfillmentDetail))
    saleManager.updateFulfillmentDetails([newFulfillmentDetail])
  }

  const useBillingAddressForShipping = () => {
    copyBillingAddressToShippingAddress()
    handleEmptyShippingCorrectBillingAddress?.()
    onClose?.()
  }

  const startUpdatingBillingAddress = () => {
    setIsClickedToOpenModal(true)
  }

  return {
    addOrUpdateAddressText: hasIncorrectShippingOrBillingAddress
      ? t('Update address', {
          _context: 'Popover button',
        })
      : t('Add new address', {
          _context: 'Modal button',
        }),
    buttonType,
    copyBillingAddressToShippingAddress,
    hasError: Boolean(error),
    hasIncorrectShippingOrBillingAddress,
    isClickedToOpenModal,
    openAddressModal,
    openEditAddressModal,
    startUpdatingBillingAddress,
    status,
    updateBillingAddressDataTrack: HEAP_DATA_TRACK_UPDATE_BILLING_ADDRESS,
    updateBillingAddressText: t('Update billing address', {
      _context: 'Modal button',
    }),
    useBillingAddressForShipping,
    useBillingAddressForShippingDataTrack: HEAP_DATA_TRACK_USE_BILLING_ADDRESS_FOR_SHIPPING,
    useBillingAddressForShippingText: t('Use billing address for shipping', {
      _context: 'Modal button',
    }),
  }
}
